import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";

export type WindowTileConfiguration = {
    id: string;
    productName: string;
    fabricColorHex: string | null;
    fabricColorName: string | null;
    frameColorHex: string | null;
    frameColorName: string | null;
};

type WindowTileProps = {
    imageUrl?: string | null;
    selected: boolean;
    onToggle: () => void;
    configurations?: WindowTileConfiguration[];
};

export function WindowTile({
    imageUrl,
    selected,
    onToggle,
    configurations,
}: WindowTileProps) {
    const { t } = useTranslation();
    return (
        <button
            type="button"
            aria-pressed={selected}
            onClick={onToggle}
            data-testid="window-tile"
            className={cn(
                "relative flex w-full flex-col items-center gap-3 rounded-xl border-2 p-4 text-center transition-all cursor-pointer",
                selected
                    ? "border-primary bg-[color-mix(in_srgb,var(--color-primary)_6%,white)]"
                    : "border-gray-200 bg-white hover:border-gray-300",
            )}
        >
            {configurations && configurations.length > 0 && (
                <div className="absolute top-2 md:top-4 right-2 md:right-4 flex max-w-[calc(100%-1rem)] flex-col items-end gap-1">
                    {configurations.map((cfg) => (
                        <div
                            key={cfg.id}
                            data-testid="window-tile-configuration"
                            className="flex min-w-0 max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white px-2 py-0.5 shadow-sm"
                        >
                            {cfg.fabricColorHex && (
                                <span
                                    data-testid="window-tile-fabric-color"
                                    title={
                                        cfg.fabricColorName
                                            ? t("colorStep.title") +
                                              ": " +
                                              cfg.fabricColorName
                                            : t("colorStep.title")
                                    }
                                    className="block size-3 shrink-0 rounded-full border border-gray-200"
                                    style={{ background: cfg.fabricColorHex }}
                                />
                            )}
                            {cfg.frameColorHex && (
                                <span
                                    data-testid="window-tile-frame-color"
                                    title={
                                        cfg.frameColorName
                                            ? t(
                                                  "colorStep.frameColorCollection",
                                              ) +
                                              ": " +
                                              cfg.frameColorName
                                            : t(
                                                  "colorStep.frameColorCollection",
                                              )
                                    }
                                    className="block size-3 shrink-0 rounded-full border border-gray-200"
                                    style={{ background: cfg.frameColorHex }}
                                />
                            )}
                            <span className="truncate text-xs font-medium text-gray-700">
                                {cfg.productName}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="flex aspect-square w-full items-center justify-center overflow-hidden rounded-lg">
                {imageUrl ? (
                    <img
                        src={imageUrl}
                        alt=""
                        className="h-full w-full object-contain p-4 md:p-8"
                    />
                ) : (
                    <div className="h-full w-full bg-gray-100" />
                )}
            </div>

            <div
                className={cn(
                    "size-6 shrink-0 rounded-full border-2 transition-colors",
                    selected ? "border-primary bg-primary" : "border-gray-300",
                )}
            >
                {selected && (
                    <svg
                        data-testid="window-tile-check"
                        viewBox="0 0 12 12"
                        fill="none"
                        className="h-full w-full p-0.5"
                    >
                        <path
                            d="M2 6l3 3 5-5"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                )}
            </div>
        </button>
    );
}
